/** @jsx jsx */
import { jsx } from '@emotion/react'
import styled from '@emotion/styled';
import React, { useCallback, useEffect, useState } from "react";
import { createRoot } from 'react-dom/client'
import { CacheProvider, css, CSSObject } from '@emotion/react'
import createCache from '@emotion/cache'

//const color = "#0096af";
const color = "#537e89";

// Narrower than this the temple does not fit: the pillars lie down as beams, each with
// its stones side by side below it. Measured on the frame, not the window, since the
// graphic sits in a column of the page.
const narrow = '@container (max-width: 640px)';
const narrowShadow = '2px 2px 5px rgba(0, 0, 0, .8)';

export const AncientFrame = styled('div')({
  containerType: 'inline-size',
});

type AncientArchitectureProps = {
  pillars: number;
  styling: string;
}
export const AncientArchitecture = styled('div')<AncientArchitectureProps>(({pillars}) => ({
    display: 'grid',
    fontSize: '200%',
    color: 'white',
    textShadow: '5px 5px 10px black',
    pageBreakInside: 'avoid',
    gridGap: '0.5em',
    minWidth: `${(pillars * 1)}em`,
    gridTemplateRows: '[roof-start] auto [roof-end pillars-start] auto [pillars-end stone-start] auto [stone-end foundation-start] auto [foundation-end]',
    gridTemplateColumns: '[all-start] '+ 'auto '.repeat(pillars) +' [all-end]',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MsUserSelect: 'none',
    [narrow]: {
      fontSize: '125%',
      textShadow: narrowShadow,
      minWidth: 0,
      gridTemplateRows: 'auto',
      gridTemplateColumns: '[all-start] minmax(0, 1fr) [all-end]',
    },
}));

type AncientRoofProps = {
  title: string
}
export const AncientRoof = styled('div')<AncientRoofProps>(({title}) => ({
  cursor: 'pointer',
  width: '100%',
  height: '150px', 
  transform: 'rotate(1deg)',
  display: 'grid',
  background: color,
  clipPath: "polygon(0% 100%, 50% 0%, 100% 100%)",
  position: 'relative',
  lineHeight: '180px',
  zIndex: 0,
  '::after': {
    color: 'white',
    position: 'relative',
    textAlign: 'center',
    content: `'${title}'`,
    zIndex: 1,
    fontSize: '120%',
  },
  ':hover': {
    transform: 'rotate(-1deg)',
  },
  [narrow]: {
    height: '90px',
    lineHeight: '122px',
    '::after': { fontSize: '100%' },
  },
}));
export const AncientRoofWrap = styled('div')({
  gridColumn: `all-start / all-end`,
  gridRow: `roof`,
  filter: "drop-shadow(5px 5px 10px black)",
  [narrow]: { gridRow: 'auto' },
})
const deg = [ -3, 1.5, -1, 2, -2.5, 1.25 ]
const posToRotate = (pos: number, hover=false, factor=1) => {
  return `rotate(${(hover?-1:1)*(deg[pos]%deg.length)*factor}deg)`
}
// lying down, a beam spans the whole width: the same tilt would lift its ends too far
const beam = 1/3;
const posToSkew = (pos: number) => {
  return `skewX(${deg[pos%deg.length]}deg)`
}

export const AncientFoundation = styled('div')((): CSSObject => ({
  //width: '400px',
  cursor: 'pointer',
  height: '60px', 
  lineHeight: '60px',
  display: 'table-cell',
  transform: posToRotate(3),
  boxShadow: '5px 5px 10px black',
  background: color,
  color: 'white',
  textAlign: 'center',
  verticalAlign: 'middle',
  gridColumn: `all-start / all-end`,
  gridRow: `foundation`,
  ':hover': {
    transform: posToRotate(3, true),
  },
  [narrow]: {
    display: 'block',
    height: 'auto',
    lineHeight: 1.2,
    padding: '14px 8px',
    gridRow: 'auto',
    transform: posToRotate(3, false, beam),
    ':hover': { transform: posToRotate(3, true, beam) },
  },
}));

type StoneProps = {
  position: number;
}
export const Stone = styled('div')<StoneProps>(({position}) => ({
  cursor: 'pointer',
  background: color,
  writingMode: 'initial',
  boxShadow: '5px 5px 10px black',
  transform: posToRotate(position),
  width: '100%',
  height: '60px',
  fontSize: '80%',
  textAlign: 'center',
  verticalAlign: 'middle',
  paddingTop: '3px',
  lineHeight: '25px',
  ':hover': {
    transform: posToRotate(position, true),
  },
  [narrow]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: 'auto',
    height: 'auto',
    minHeight: '56px',
    padding: '6px 4px',
    lineHeight: 1.2,
    overflowWrap: 'anywhere',
    boxShadow: '3px 3px 7px black',
    transform: posToRotate(position, false, beam),
    ':hover': { transform: posToRotate(position, true, beam) },
  },
}));


type AncientPillarsProps = {}
export const AncientPillars = styled('div')<AncientPillarsProps>(({}) => ({
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  flexDirection: 'row',
  alignContent: 'stretch',
  pageBreakInside: 'avoid',
  gridGap: '0.5em',
  justifyContent: 'space-evenly',
}));

type AncientPillarFrameProps = {}
export const AncientPillarFrame = styled('div')<AncientPillarFrameProps>(({}) => ({
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  flexDirection: 'column',
  alignContent: 'stretch',
  pageBreakInside: 'avoid',
  gridGap: '0.5em',
  justifyContent: 'space-evenly',
  [narrow]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    alignItems: 'stretch',
    gridGap: '0.45em',
    marginTop: '0.3em',
  },
}));

type AncientPillarProps = {
  position: number;
}
export const AncientPillar = styled('div')<AncientPillarProps>(({position}) => ({
  '::before': {
    content: '""',
    transform: posToSkew(position),
    background: color,
    width: '30px',
    position: 'absolute',
    left: '-10px',
    top: 0,
    bottom: 0,
    zIndex: -1,
  },
  '::after': {
    content: '""',
    transform: posToSkew(position+2),
    background: color,
    width: '30px',
    position: 'absolute',
    right: '-10px',
    top: 0,
    bottom: 0,
    zIndex: -1,
  },
  cursor: 'pointer',
  writingMode: 'vertical-lr',
  position: 'relative',
  textShadow: '5px -5px 10px black',
  filter: "drop-shadow(5px 5px 10px black)",
  transform: posToRotate(position+1),
  zIndex: 1,
  background: color,
  width: '50px',
  height: '400px',
  display: 'inline',
  textAlign: 'center',
  lineHeight: '50px',
  ':hover': {
    transform: posToRotate(position+1, true),
  },
  [narrow]: {
    '::before': { display: 'none' },
    '::after': { display: 'none' },
    display: 'block',
    gridColumn: '1 / -1',
    writingMode: 'horizontal-tb',
    width: 'auto',
    height: 'auto',
    lineHeight: 1.2,
    padding: '12px 8px',
    textShadow: narrowShadow,
    filter: 'drop-shadow(3px 3px 7px black)',
    transform: posToRotate(position+1, false, beam),
    ':hover': { transform: posToRotate(position+1, true, beam) },
  },
}));

type ElementType = {
  title: string;
  link?: string;
}
type PillarType = {
  title: string;
  link?: string;
  stones: ElementType[];
}
type ArchitectureType = {
  roof: ElementType;
  pillars: PillarType[];
  foundation: ElementType;
}
type ArchitectureProps = {
  data: ArchitectureType;
  styleRoot: Element;
}

export const Architecture = ({data, styleRoot}: ArchitectureProps) => {
  const myCache = createCache({
    key: 'arch', container: styleRoot
  })
  const navigate = (target: string|undefined|null) => {
    if(typeof target === 'string') {
      location.href = target
    }
  }
  useEffect(() => {
    return () => {
      myCache.sheet.flush();
      (myCache.inserted as any) = [];
      myCache.registered = {};
    }
  })
  return (
    <CacheProvider value={myCache}>
      <AncientFrame>
      <AncientArchitecture styling="modern" pillars={data.pillars.length}>
        <AncientRoofWrap>
          <AncientRoof title={data.roof?.title ?? data.roof} onClick={() => navigate(data.roof.link)}/>
        </AncientRoofWrap>
        {data.pillars.map((pillar, idx) =>
          <AncientPillarFrame key={idx}>
            <AncientPillar position={idx+1} onClick={() => navigate(pillar.link)} title={pillar.title}>{pillar.title}</AncientPillar>
            { pillar.stones.map((stone, idx2) => <Stone key={idx2} position={idx} onClick={() => navigate(stone.link)} dangerouslySetInnerHTML={{ __html: (stone?.title ?? stone).replace('\n', '<br />') }} />)}
          </AncientPillarFrame>
        )}
        <AncientFoundation onClick={() => navigate(data.foundation.link)}>{data.foundation?.title ?? data.foundation}</AncientFoundation>
      </AncientArchitecture>
      </AncientFrame>
    </CacheProvider>
  )
}

export const AncientTest = () => {
  // Before REACT start, clean Frames
  const test = document.getElementById('test');
  if (test) {
    test.innerHTML = "";
  }
  createRoot(document.getElementById('col1_content')!).render(<AncientArchitecture styling="modern" pillars={4}>
      <AncientRoof
        title="Modern-Computing"
      >
      </AncientRoof>
      <AncientPillarFrame>
        <AncientPillar
          title="Embedded Systems"
          position={1}
        >
          Embedded Systems
        </AncientPillar>
        <Stone
          position={1}>
          HW/SW-Codesign
        </Stone>
        <Stone
          position={1}>
          Embedded Systems Robotic
        </Stone>
      </AncientPillarFrame>
      <AncientPillarFrame>
        <AncientPillar
          title="Digital Computing"
          position={2}
        >
          Digital Computing
        </AncientPillar>

        <Stone
          position={2}
        >IC-Design</Stone>
        <Stone
          position={2}
        >Computer Achitecture</Stone>
      </AncientPillarFrame>
      <AncientPillarFrame>
        <AncientPillar
          title="Trustworthy Computing"
          position={3}
        >
          Trustworthy Computing
        </AncientPillar>
        <Stone position={3}>Reliability</Stone>
        <Stone position={3}>Security</Stone>
        <Stone position={3}>Confidentiality</Stone>
      </AncientPillarFrame>
      <AncientPillarFrame>
        <AncientPillar
          title="Post Moore"
          position={4}
        >
          Post Moore
        </AncientPillar>
        <Stone position={4}>In Memory Computing</Stone>
        <Stone position={4}>Heterogenious Computing</Stone>
      </AncientPillarFrame>
      <AncientFoundation>Edge-AI-Computing</AncientFoundation>
    </AncientArchitecture>);
}


/*
export function AncientArchitecture(props: any) {
  return (
    <AncientGrid>

    </AncientGrid>
  )
}
*/

/*
<AncientArchitecture style="modern">
  <AncientRoof title="Modern-Computing">
    <Stone>HW/SW-Codesign</Stone>
    <Stone>EmbeddedSystems in Robotic</Stone>
  </AncientRoof>
  <AncientPilar title="Embedded Systems">
    <Stone>IC-Design</Stone>
    <Stone>Computer Achitecture<Stone>
  </AncientPilar>
  <AncientPilar title="Digital Computing">
    <Stone>Reliability</Stone>
    <Stone>Security/Confidentiality</Stone
  </AncientPilar>
  <AncientPilar title="Dependability">
    <Stone>In Memory Computing</Stone>
    <Stone>Heterogenious Computing</Stone>
  </AncientPilar>
  <AncientPilar title="Post Moore">
  </AncientPilar>
  <AncientFoundation>Edge-AI-Computing</AncientFoundation>
</AncientArchitecture>

*/